import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Stack, TextField, Alert, CircularProgress,
} from "@mui/material";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { useDropStudentMutation, useReactivateStudentMutation } from "../../../../Api/attoDeskApi";

const todayISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

type DropDialogProps = {
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  onSuccess?: () => void;
};

/**
 * Drop Student modal. Requires drop_start_date + drop_reason. Prevents
 * double-submits during the mutation. Backend refuses (409) if the
 * student is already dropped; that message surfaces here without a
 * duplicate frontend gate.
 */
export const DropStudentDialog: React.FC<DropDialogProps> = ({ open, onClose, studentId, studentName, onSuccess }) => {
  const [drop, { isLoading }] = useDropStudentMutation();
  const [dropDate, setDropDate] = useState<string>(todayISO());
  const [reason, setReason] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const reasonInvalid = touched && reason.trim().length === 0;
  const dateInvalid = touched && !dropDate;

  const handleClose = () => {
    if (isLoading) return;
    setReason(""); setDropDate(todayISO()); setTouched(false); setApiError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!reason.trim() || !dropDate) { setTouched(true); return; }
    setApiError(null);
    try {
      const res = await drop({ student_id: studentId, drop_start_date: dropDate, drop_reason: reason.trim() }).unwrap();
      if ((res as any)?.status === false) { setApiError((res as any)?.message ?? "Failed to drop student."); return; }
      onSuccess?.();
      handleClose();
    } catch (e: any) {
      setApiError(e?.data?.message ?? "Failed to drop student.");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
        <PersonOffOutlinedIcon sx={{ color: "#ef4444" }} />
        Drop Student — {studentName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 13.5 }}>
          The student record is NOT deleted. Historical attendance, invoices, classes and session data remain
          visible. A record of this drop (date + reason + you as the actor) is preserved in the History tab.
          You can reactivate this student later at any time.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label="Drop Start Date"
            type="date"
            value={dropDate}
            onChange={(e) => setDropDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            error={dateInvalid}
            helperText={dateInvalid ? "Drop date is required." : " "}
            fullWidth
          />
          <TextField
            label="Drop Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Enter the reason this student is being dropped..."
            required
            multiline minRows={3}
            error={reasonInvalid}
            helperText={reasonInvalid ? "Reason is required." : "Captured in the audit trail alongside the effective date."}
            fullWidth
          />
          {apiError && <Alert severity="error" sx={{ fontSize: 13 }}>{apiError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: "none", fontWeight: 700, color: "#475569" }}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          variant="contained"
          color="error"
          startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PersonOffOutlinedIcon />}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {isLoading ? "Dropping…" : "Confirm Drop"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ReactivateDialogProps = {
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  onSuccess?: () => void;
};

export const ReactivateStudentDialog: React.FC<ReactivateDialogProps> = ({ open, onClose, studentId, studentName, onSuccess }) => {
  const [reactivate, { isLoading }] = useReactivateStudentMutation();
  const [date, setDate] = useState<string>(todayISO());
  const [reason, setReason] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const reasonInvalid = touched && reason.trim().length === 0;
  const dateInvalid = touched && !date;

  const handleClose = () => {
    if (isLoading) return;
    setReason(""); setDate(todayISO()); setTouched(false); setApiError(null);
    onClose();
  };
  const handleConfirm = async () => {
    if (!reason.trim() || !date) { setTouched(true); return; }
    setApiError(null);
    try {
      const res = await reactivate({ student_id: studentId, reactivation_date: date, reason: reason.trim() }).unwrap();
      if ((res as any)?.status === false) { setApiError((res as any)?.message ?? "Failed to reactivate student."); return; }
      onSuccess?.();
      handleClose();
    } catch (e: any) {
      setApiError(e?.data?.message ?? "Failed to reactivate student.");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
        <HowToRegOutlinedIcon sx={{ color: "#16a34a" }} />
        Reactivate Student — {studentName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 13.5 }}>
          The current drop record is closed and the student becomes Active again. Every prior drop and
          reactivation event is preserved in the History tab — nothing is erased or overwritten.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label="Reactivation Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            error={dateInvalid}
            helperText={dateInvalid ? "Reactivation date is required." : " "}
            fullWidth
          />
          <TextField
            label="Reason / Note"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. Returned to program at parent's request"
            required
            multiline minRows={3}
            error={reasonInvalid}
            helperText={reasonInvalid ? "Reason is required." : "Preserved on the timeline alongside the effective date."}
            fullWidth
          />
          {apiError && <Alert severity="error" sx={{ fontSize: 13 }}>{apiError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: "none", fontWeight: 700, color: "#475569" }}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          variant="contained"
          color="success"
          startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <HowToRegOutlinedIcon />}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {isLoading ? "Reactivating…" : "Confirm Reactivation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
